import { store$ } from '@redux/store';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import { selectLayoutBreakpoint } from 'layout/redux/selectors';
import { combineLatest, distinctUntilChanged, fromEvent, map } from 'rxjs';

/**
 * Adds a CSS class to the `<body>` element to prevent scrolling.
 * 
 * This function applies the `nhs-u-overflow--hidden` class to the `<body>` 
 * element, which is typically styled to disable scrolling (e.g., `overflow: hidden`).
 */
const bodyNoScrollable = () => document.body.classList.add('nhs-u-overflow--hidden');

/**
 * Removes the CSS class from the `<body>` element to allow scrolling.
 * 
 * This function removes the `nhs-u-overflow--hidden` class from the `<body>` 
 * element, re-enabling scrolling behavior if it was previously disabled.
 */
const bodyScrollable = () => document.body.classList.remove('nhs-u-overflow--hidden');

/**
 * Handles the state of the hamburger menu and controls the `<body>`'s scrollability.
 * 
 * This function determines whether to disable scrolling on the `<body>` based on 
 * the state of the hamburger menu (`isHamburgerMenuOpen`) and the current layout 
 * breakpoint (`layoutBreakPoint`). If the menu is open and the layout breakpoint 
 * is smaller than `LayoutBreakpoint.Big`, scrolling is disabled.
 * 
 * @param {Object} options.layoutBreakPoint - The current layout breakpoint (e.g., "Small", "Medium", "Big").
 * @param {boolean} options.isHamburgerMenuOpen - Whether the hamburger menu is open.
 */
const handlerHamburgerMenu = ({ layoutBreakPoint, isHamburgerMenuOpen }) => {
  if (isHamburgerMenuOpen && layoutBreakPoint !== LayoutBreakpoint.Big) {
    bodyNoScrollable();
    return;
  }

  bodyScrollable();
}

const onHamburgerMenuClicked$ = fromEvent(document.body, 'hamburgerMenuClicked').pipe(
  map((event) => event.detail.data.isOpen)
);

const onLayoutBreakPointChanged$ = store$.pipe(
  map(selectLayoutBreakpoint),
  distinctUntilChanged(),
)

combineLatest([onLayoutBreakPointChanged$, onHamburgerMenuClicked$]).pipe(
  map(([layoutBreakPoint, isHamburgerMenuOpen]) => ({
    layoutBreakPoint, isHamburgerMenuOpen
  }))
).subscribe(handlerHamburgerMenu)
